import { computed, nextTick, ref } from "vue";
import { VIEW_STATES, CSS_CLASSES } from "@/viewer/constants.js";
import { handleParentIframeClasses } from "@/viewer/utils.js";

/**
 * Vue Composable: Viewer State Management
 *
 * This composable encapsulates all logic related to managing the IIIF viewer's
 * state transitions between preview, modal, and fullscreen modes. It provides
 * reactive state tracking and methods for state transitions with proper cleanup.
 *
 * State Flow:
 * PREVIEW ←→ MODAL ←→ FULLSCREEN
 *
 * @returns {Object} Reactive state and transition methods
 */
export function useViewerState() {
    // REACTIVE STATE

    /**
     * Current view state of the viewer
     * @type {import('vue').Ref<string>}
     */
    const currentViewState = ref(VIEW_STATES.PREVIEW);

    // COMPUTED STATE FLAGS

    /**
     * True when viewer is in preview mode (showing thumbnail)
     * @type {import('vue').ComputedRef<boolean>}
     */
    const isPreviewState = computed(
        () => currentViewState.value === VIEW_STATES.PREVIEW
    );

    /**
     * True when viewer is in modal overlay mode
     * @type {import('vue').ComputedRef<boolean>}
     */
    const isModalState = computed(
        () => currentViewState.value === VIEW_STATES.MODAL
    );

    /**
     * True when viewer is in fullscreen mode
     * @type {import('vue').ComputedRef<boolean>}
     */
    const isFullscreenState = computed(
        () => currentViewState.value === VIEW_STATES.FULLSCREEN
    );

    // VIEWER SETTINGS MANAGEMENT

    /**
     * Update viewer settings after state change
     *
     * Controls navigation visibility based on current state. Navigation is hidden
     * in preview mode and shown in modal/fullscreen modes. This ensures the UI
     * is appropriate for each viewing context.
     *
     * @param {import('vue').Ref} viewerRef - Reference to the Glycerine viewer component
     */
    function updateViewerSettings(viewerRef) {
        if (!viewerRef?.value) return;

        const nav = viewerRef.value.$el.querySelector(".anno-gallery-nav");
        if (nav) {
            const showNav = currentViewState.value !== VIEW_STATES.PREVIEW;
            nav.style.display = showNav ? "block" : "none";
            viewerRef.value.initNavigation();
        }
    }

    // STATE TRANSITION METHODS

    /**
     * Transition from preview to modal state
     *
     * Opens the viewer in modal overlay mode with the following actions:
     * - Updates internal state to MODAL
     * - Adds CSS classes to parent iframe for styling
     * - Adds fullscreen class to document body
     * - Updates viewer settings asynchronously
     *
     * @param {import('vue').Ref} viewerRef - Reference to the viewer component
     */
    function openModal(viewerRef) {
        currentViewState.value = VIEW_STATES.MODAL;
        handleParentIframeClasses("add");
        document.body.classList.add(CSS_CLASSES.IFRAME_PARTIAL_FULLSCREEN);
        nextTick(() => updateViewerSettings(viewerRef));
    }

    /**
     * Return to preview state from any other state
     *
     * Performs comprehensive cleanup when closing the viewer:
     * - Updates internal state to PREVIEW
     * - Removes CSS classes from parent iframe
     * - Removes fullscreen class from document body
     * - Exits browser fullscreen if active
     * - Reinitializes DOM observer for next modal open
     * - Updates viewer settings asynchronously
     *
     * @param {import('vue').Ref} viewerRef - Reference to the viewer component
     * @param {Function} [observeDOM] - Optional DOM observer initialization function
     */
    function closeToPreview(viewerRef, observeDOM) {
        currentViewState.value = VIEW_STATES.PREVIEW;
        handleParentIframeClasses("remove");
        document.body.classList.remove(CSS_CLASSES.IFRAME_PARTIAL_FULLSCREEN);

        viewerRef.value?.$el
            ?.querySelectorAll("video")
            .forEach((v) => v.pause());

        // Exit browser fullscreen if currently active
        if (document.fullscreenElement && document.exitFullscreen) {
            document.exitFullscreen();
        }

        // Reinitialize DOM observer for next modal session
        if (observeDOM) {
            observeDOM();
        }

        nextTick(() => updateViewerSettings(viewerRef));
    }

    /**
     * Transition to fullscreen state
     *
     * This method is typically called in response to external fullscreen events
     * rather than user interaction. The actual fullscreen API calls are handled
     * elsewhere, this just updates the internal state tracking.
     */
    function enterFullscreen() {
        currentViewState.value = VIEW_STATES.FULLSCREEN;
    }

    // PUBLIC API

    return {
        // Reactive State
        currentViewState,

        // Computed State Flags
        isPreviewState,
        isModalState,
        isFullscreenState,

        // State Management Methods
        updateViewerSettings,
        openModal,
        closeToPreview,
        enterFullscreen,
    };
}
